import { type ResolvedWishlistEntryModel } from "~/Wishlist/model";
import { type Table } from "@tanstack/react-table";
import { PriceSummary } from "~/Wishlist/page/WishlistPage/components/WishlistDetails/components/PriceSummary/PriceSummary";
import { useSelector } from "react-redux";
import { type RootState } from "~/redux/store";
import { getSelectPriceParamsFromWishlistEntry } from "~/Wishlist/redux/wishlistSelectors";
import { getTotalPrice, selectNetPrice } from "~/feature/ProductDescriptor/v1/productDescriptorUtils";
import { sum, text, volatileSum } from "~/utils/PriceUtils";
import { useTranslation } from "react-i18next";

interface Props {
  className?: string;
  tableRef: Table<ResolvedWishlistEntryModel>;
}
export const WishlistPriceSummary = ({ tableRef, className }: Props) => {
  const { t } = useTranslation();
  const allItems = tableRef.getCoreRowModel().rows.map((row) => row.original);
  const selectedItems = tableRef.getSelectedRowModel().rows.map((row) => row.original);

  const totalPrice = useSelector(selectPriceOfEntries(allItems));
  const totalSurcharge = useSelector(selectSurchargeOfEntries(allItems));
  const selectedPrice = useSelector(selectPriceOfEntries(selectedItems));
  const selectedSurcharge = useSelector(selectSurchargeOfEntries(selectedItems));
  const totalHasAnySurcharge = useSelector(hasAnySurcharge(allItems));
  const selectedHasAnySurcharge = useSelector(hasAnySurcharge(selectedItems));

  const showTotalSurcharge = totalHasAnySurcharge && totalPrice.value > 0;
  const showSelectedSurcharge = selectedHasAnySurcharge && selectedPrice.value > 0;

  return (
    <PriceSummary className={className}>
      <PriceSummary.Entry title={t("page.wishlist.totals.full")}>
        {text(totalPrice)}
        {showTotalSurcharge && "*"}
      </PriceSummary.Entry>
      {showTotalSurcharge && (
        <PriceSummary.Entry title={t("page.wishlist.totals.surcharge.full")} variant={"small"}>
          {text(totalSurcharge)}
        </PriceSummary.Entry>
      )}
      <PriceSummary.Entry title={t("page.wishlist.totals.selected")} bold>
        {text(selectedPrice)}
        {showSelectedSurcharge && "*"}
      </PriceSummary.Entry>
      {showSelectedSurcharge && (
        <PriceSummary.Entry title={t("page.wishlist.totals.surcharge.selected")} variant={"small"}>
          {text(selectedSurcharge)}
        </PriceSummary.Entry>
      )}
    </PriceSummary>
  );
};

const selectPriceOfEntries = (entries: ResolvedWishlistEntryModel[]) => (state: RootState) => {
  return volatileSum(
    ...entries.map((entry) => {
      const priceParams = getSelectPriceParamsFromWishlistEntry(entry);
      const netPrice = selectNetPrice(priceParams)(state);

      return getTotalPrice(netPrice, priceParams.extraQuantity);
    })
  );
};
const hasAnySurcharge = (entries: ResolvedWishlistEntryModel[]) => (state: RootState) => {
  return entries.some((entry) => {
    const priceParams = getSelectPriceParamsFromWishlistEntry(entry);
    const netPrice = selectNetPrice(priceParams)(state);

    return netPrice?.surcharge ?? 0 > 0;
  });
};

const selectSurchargeOfEntries = (entries: ResolvedWishlistEntryModel[]) => (state: RootState) => {
  return sum(
    ...entries.map((entry) => {
      const priceParams = getSelectPriceParamsFromWishlistEntry(entry);
      const netPrice = selectNetPrice(priceParams)(state);
      const totalPrice = getTotalPrice(netPrice, priceParams.extraQuantity);

      return {
        currencyIso: totalPrice.currencyIso,
        value: totalPrice.value - totalPrice.baseValue,
      };
    })
  );
};
